import type { PullRequestCockpit } from '@crowdex/workspace-protocol/pull-request-cockpit';
import { useCallback, useEffect, useSyncExternalStore } from 'react';

type Snapshot = {
  data: PullRequestCockpit | null;
  error: string | null;
  loading: boolean;
  updatedAt: number | null;
};

type Entry = {
  snapshot: Snapshot;
  listeners: Set<() => void>;
  loader: ((signal: AbortSignal) => Promise<PullRequestCockpit | null>) | null;
  promise: Promise<void> | null;
  request: number;
  version: string | null;
  revision: string | null;
  controller: AbortController | null;
  attemptedAt: number | null;
  failures: number;
  retryAt: number;
};

const EMPTY: Snapshot = { data: null, error: null, loading: false, updatedAt: null };
const MAX_CACHED_ENTRIES = 128;
const entries = new Map<string, Entry>();

function pruneEntries(protectedEntry?: Entry): void {
  while (entries.size > MAX_CACHED_ENTRIES) {
    const stale = [...entries].find(
      ([, entry]) => entry !== protectedEntry && entry.listeners.size === 0,
    );
    if (stale === undefined) return;
    cancelEntry(stale[1]);
    stale[1].loader = null;
    entries.delete(stale[0]);
  }
}

function entryFor(key: string): Entry {
  const existing = entries.get(key);
  if (existing !== undefined) {
    // Map insertion order is the LRU order used by pruneEntries.
    entries.delete(key);
    entries.set(key, existing);
    return existing;
  }
  const entry: Entry = {
    snapshot: EMPTY,
    listeners: new Set(),
    loader: null,
    promise: null,
    request: 0,
    version: null,
    revision: null,
    controller: null,
    attemptedAt: null,
    failures: 0,
    retryAt: 0,
  };
  entries.set(key, entry);
  pruneEntries(entry);
  return entry;
}

function publish(entry: Entry, snapshot: Snapshot): void {
  entry.snapshot = snapshot;
  for (const listener of entry.listeners) listener();
}

async function loadEntry(entry: Entry, force: boolean): Promise<void> {
  if (entry.loader === null) return;
  if (Date.now() < entry.retryAt) return;
  if (!force && entry.attemptedAt !== null) return;
  if (entry.promise !== null) return entry.promise;
  const request = ++entry.request;
  const controller = new AbortController();
  const loader = entry.loader;
  entry.controller = controller;
  entry.attemptedAt = Date.now();
  publish(entry, { ...entry.snapshot, loading: true, error: null });
  entry.promise = Promise.resolve()
    .then(() => loader(controller.signal))
    .then((data) => {
      if (request !== entry.request) return;
      entry.failures = 0;
      entry.retryAt = 0;
      entry.attemptedAt = Date.now();
      publish(entry, {
        data,
        error: null,
        loading: false,
        updatedAt: Date.now(),
      });
    })
    .catch((error: unknown) => {
      if (request !== entry.request) return;
      const message = error instanceof Error ? error.message : String(error);
      const limited = /rate[ -]?limit|abuse detection/i.test(message);
      entry.failures += 1;
      entry.retryAt =
        Date.now() +
        Math.min(
          limited ? 3_600_000 : 300_000,
          (limited ? 300_000 : 60_000) * 2 ** Math.min(entry.failures - 1, 6),
        );
      entry.attemptedAt = Date.now();
      publish(entry, {
        data: entry.snapshot.data,
        error: message,
        loading: false,
        updatedAt: entry.snapshot.updatedAt,
      });
    })
    .finally(() => {
      if (request === entry.request) {
        entry.promise = null;
        entry.controller = null;
      }
    });
  return entry.promise;
}

function cancelEntry(entry: Entry): void {
  if (entry.promise !== null) entry.attemptedAt = null;
  entry.request += 1;
  entry.controller?.abort();
  entry.controller = null;
  entry.promise = null;
  if (entry.snapshot.loading) publish(entry, { ...entry.snapshot, loading: false });
}

export function cockpitRefreshInterval(
  data: PullRequestCockpit | null,
  idleMs = 60_000,
): number | false {
  if (data?.pullRequest.state === 'closed' || data?.pullRequest.state === 'merged') return false;
  return data?.checks.some((check) => check.status === 'queued' || check.status === 'in_progress')
    ? Math.min(15_000, idleMs)
    : idleMs;
}

export async function invalidatePullRequestCockpit(key?: string): Promise<void> {
  const targets =
    key === undefined ? [...entries.values()] : entries.has(key) ? [entryFor(key)] : [];
  await Promise.all(
    targets.map(async (entry) => {
      cancelEntry(entry);
      entry.attemptedAt = null;
      publish(entry, EMPTY);
      if (
        entry.listeners.size > 0 &&
        entry.loader !== null &&
        document.visibilityState !== 'hidden'
      )
        await loadEntry(entry, true);
    }),
  );
}

export function usePullRequestCockpitQuery({
  enabled,
  key,
  load,
  pollMs = 60_000,
  version = null,
  revision = null,
}: {
  enabled: boolean;
  key: string | null;
  load: ((signal: AbortSignal) => Promise<PullRequestCockpit | null>) | undefined;
  pollMs?: number;
  version?: string | null;
  revision?: string | null;
}): Snapshot & { refresh: () => Promise<void> } {
  const activeKey = enabled ? key : null;
  const entry = activeKey === null ? null : entryFor(activeKey);
  if (entry !== null) entry.loader = load ?? null;
  const subscribe = useCallback(
    (listener: () => void) => {
      if (entry === null) return () => undefined;
      // React Strict Mode replays subscribe cleanup/setup without rerendering.
      // Restore the current hook's loader before the replayed effects run.
      entry.loader = load ?? null;
      entry.listeners.add(listener);
      return () => {
        entry.listeners.delete(listener);
        if (entry.listeners.size === 0) {
          cancelEntry(entry);
          // Do not retain component/workspace closures while keeping the reusable snapshot.
          entry.loader = null;
          pruneEntries();
        }
      };
    },
    [entry, load],
  );
  const getSnapshot = useCallback(() => entry?.snapshot ?? EMPTY, [entry]);
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (entry === null || entry.loader === null) return;
    const versionChanged = version !== null && entry.version !== null && entry.version !== version;
    const revisionChanged =
      revision !== null && entry.revision !== null && entry.revision !== revision;
    if (version !== null) entry.version = version;
    if (revision !== null) entry.revision = revision;
    if (versionChanged || revisionChanged) {
      cancelEntry(entry);
      entry.attemptedAt = null;
      publish(entry, EMPTY);
    }
    const refresh = (): void => {
      if (document.visibilityState === 'hidden') return;
      const delay = cockpitRefreshInterval(entry.snapshot.data, pollMs);
      const stale =
        delay !== false &&
        pollMs > 0 &&
        (entry.attemptedAt === null || Date.now() - entry.attemptedAt >= delay);
      void loadEntry(entry, entry.attemptedAt === null || stale);
    };
    refresh();
    if (pollMs <= 0) return;
    const interval = window.setInterval(refresh, Math.min(15_000, pollMs));
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [entry, pollMs, version, revision]);

  return {
    ...snapshot,
    refresh: useCallback(
      () => (entry === null ? Promise.resolve() : loadEntry(entry, true)),
      [entry],
    ),
  };
}
